/** Морф плитки в карточку товара.
 *
 * Открытие товара — не переход на новую страницу, а расширение того же
 * предмета: фото плитки разъезжается до фото полной карточки.
 *
 * Приём FLIP: клон сразу ставится на место цели и нужного размера, а к нему
 * применяется обратное преобразование — так он выглядит как исходная плитка.
 * Дальше преобразование снимается, и браузер доводит клон до цели.
 *
 * Важно, что едет только `transform`. Первая версия анимировала `width`,
 * `height` и `blur`: каждый кадр браузер пересчитывал макет и заново размывал
 * картинку, и на телефоне полёт получался рваным. Трансформация же уходит на
 * композитор и идёт ровно, даже когда основной поток занят отрисовкой карточки.
 */

interface Source {
  rect: DOMRect;
  src: string | null;
}

/** Откуда «раскрывается» карточка: живёт между кликом и отрисовкой товара. */
let pending: Source | null = null;

const reduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Запомнить нажатую плитку. Вызывается в обработчике тапа, до навигации. */
export function rememberTile(element: HTMLElement | null, fullImage: string | null): void {
  pending = null;
  if (element && !reduced()) {
    const image = element.querySelector('img');
    pending = {
      rect: element.getBoundingClientRect(),
      src: image ? image.currentSrc || image.src : null,
    };
  }
  // Полноразмерное фото начинает грузиться, пока клон летит
  if (fullImage) {
    const preload = new Image();
    preload.src = fullImage;
  }
}

/** Доиграть расширение до фото карточки. Без запомненной плитки — ничего. */
export function runMorph(target: HTMLElement | null): void {
  const from = pending;
  pending = null;
  if (!from || !target) return;

  window.scrollTo(0, 0); // товар всегда открывается сверху — меряем цель уже там
  const to = target.getBoundingClientRect();
  if (!to.width || !to.height) return;

  // Обратное преобразование: клон стоит на цели, но выглядит как плитка
  const dx = from.rect.left - to.left;
  const dy = from.rect.top - to.top;
  const sx = from.rect.width / to.width;
  const sy = from.rect.height / to.height;

  const clone = document.createElement('div');
  clone.style.cssText =
    'position:fixed;z-index:40;pointer-events:none;overflow:hidden;' +
    'border-radius:20px;background:#0e1b2a;background-size:cover;background-position:center;' +
    'box-shadow:0 18px 50px rgba(0,0,0,.45);transform-origin:top left;' +
    `left:${to.left}px;top:${to.top}px;width:${to.width}px;height:${to.height}px;` +
    `transform:translate(${dx}px,${dy}px) scale(${sx},${sy});`;
  if (from.src) clone.style.backgroundImage = `url("${from.src}")`;
  document.body.appendChild(clone);

  // Фото карточки прячем, пока клон не долетит; остальное мягко проступает
  target.style.opacity = '0';

  // Форс-рефлоу, затем цель перехода — так анимация точно стартует
  void clone.offsetWidth;
  clone.style.transition = 'transform .3s cubic-bezier(.22,.61,.36,1)';
  clone.style.transform = 'none';

  let finished = false;

  /** Убрать клон и вернуть фото карточки. `hard` — немедленно, без растворения. */
  const finish = (hard: boolean) => {
    if (finished) return;
    finished = true;
    window.removeEventListener('scroll', bail);

    // Карточка проступает ПОД клоном, и только потом клон растворяется:
    // мгновенное удаление оставляло кадр без фото — картинка моргала
    target.style.opacity = '';
    if (hard) {
      clone.remove();
      return;
    }
    clone.style.transition = 'opacity .16s linear';
    clone.style.opacity = '0';
    setTimeout(() => clone.remove(), 190);
  };

  /* Клон висит в `fixed`, то есть не едет со страницей. Стоит человеку начать
     листать карточку, пока фото ещё декодируется, — и клон остаётся приклеенным
     к экрану поверх текста. Первое открытие товара как раз медленное: снимок не
     в кэше. Поэтому любая прокрутка немедленно снимает клон. */
  const bail = () => {
    // scrollTo выше сам порождает событие прокрутки, и оно приходит уже после
    // подписки. Реагируем только на настоящий сдвиг, иначе морф умирает,
    // не начавшись.
    if (window.scrollY !== 0) finish(true);
  };
  window.addEventListener('scroll', bail, { passive: true });

  let ran = false;
  const done = () => {
    if (ran) return;
    ran = true;

    // Полноразмерное фото декодируем заранее — иначе под клоном пусто
    const decoded =
      target instanceof HTMLImageElement && target.decode
        ? target.decode().catch(() => {})
        : Promise.resolve();
    Promise.race([decoded, new Promise((r) => setTimeout(r, 400))]).then(() =>
      finish(false),
    );
  };

  clone.addEventListener('transitionend', done, { once: true });
  setTimeout(done, 460); // страховка, если transitionend не придёт
}
